// Dashboard navigation configuration
// -----------------------------------
// Extracted from the previous monolithic MainDashboard.tsx so the menu/role
// model can be unit-tested and reused independently of the layout shell.
// Adding/removing pages: edit ALL_MENU_ITEMS and the DashboardComponent union.
import React from 'react';
import {
  DashboardOutlined, BankOutlined, ReadOutlined, BuildOutlined, HomeOutlined,
  TeamOutlined, WarningOutlined, SafetyCertificateOutlined, IdcardOutlined,
  AuditOutlined, DollarOutlined, FundOutlined, FilePdfOutlined, CreditCardOutlined,
  BarChartOutlined, ToolOutlined, ProjectOutlined, CarOutlined, CalendarOutlined,
  SoundOutlined, FileTextOutlined, LockOutlined,
  SettingOutlined, FileDoneOutlined,
} from '@ant-design/icons';

import { DEFAULT_FEATURE_SETTINGS } from '../../store/authStore';

export type DashboardComponent =
  | 'overview' | 'condos' | 'owners' | 'fee-management' | 'reports-analytics'
  | 'documents' | 'financial-management' | 'statements' | 'portal' | 'my-home'
  | 'payments' | 'status-certificates' | 'violations' | 'maintenance-hub'
  | 'amenities' | 'board' | 'property-operations' | 'feature-permissions'
  | 'feature-toggles' | 'agm-packets'
  | 'tutorial' | 'admin-operations' | 'communications' | 'units'
  | 'board-member-portal';

export type Permission =
  | 'all' | 'super_admin_condo' | 'admin' | 'owner' | 'board-member';

export interface MenuItem {
  key: DashboardComponent;
  icon: React.ReactNode;
  label: string;
  group: MenuGroupKey;
  permission: Permission;
}

export type MenuGroupKey =
  | 'Overview' | 'My Home' | 'Property' | 'Residents'
  | 'Finances' | 'Operations' | 'System';

export interface MenuGroup {
  label: string;
  key: MenuGroupKey;
}

export const ALL_MENU_ITEMS: MenuItem[] = [
  // Overview
  { key: 'overview',             icon: <DashboardOutlined />,         label: 'Dashboard',            group: 'Overview',    permission: 'admin'             },
  { key: 'board-member-portal',  icon: <BankOutlined />,              label: 'Board Portal',         group: 'Overview',    permission: 'board-member'      },
  { key: 'tutorial',             icon: <ReadOutlined />,              label: 'Learning Centre',      group: 'Overview',    permission: 'all'               },
  // Property
  { key: 'condos',               icon: <BuildOutlined />,             label: 'Condominiums',         group: 'Property',    permission: 'super_admin_condo' },
  { key: 'units',                icon: <HomeOutlined />,              label: 'Unit Management',      group: 'Property',    permission: 'admin'             },
  // Residents
  { key: 'owners',               icon: <TeamOutlined />,              label: 'Resident Management',  group: 'Residents',   permission: 'board-member'      },
  { key: 'violations',           icon: <WarningOutlined />,           label: 'Violations',           group: 'Residents',   permission: 'board-member'      },
  { key: 'board',                icon: <SafetyCertificateOutlined />, label: 'Board Management',     group: 'Residents',   permission: 'board-member'      },
  { key: 'portal',               icon: <IdcardOutlined />,            label: 'Resident Portal',      group: 'Residents',   permission: 'admin'             },
  { key: 'status-certificates',  icon: <AuditOutlined />,             label: 'Status Certificates',  group: 'Residents',   permission: 'admin'             },
  // Sprint 90 — F42 AGM packet builder frontend. Server-side PDF
  // (AgmPacketService, Sprint 44); per-condo capability gated on the
  // agm_packet_bulk flag inside the page itself. Governance surface →
  // board-member+ (board, admin, super-admin); not owners.
  { key: 'agm-packets',          icon: <FileDoneOutlined />,          label: 'AGM Packets',          group: 'Residents',   permission: 'board-member'      },
  // My Home (owner only)
  { key: 'my-home',              icon: <HomeOutlined />,              label: 'My Home',              group: 'My Home',     permission: 'owner'             },
  // Finances
  { key: 'fee-management',       icon: <DollarOutlined />,            label: 'Fee Management',       group: 'Finances',    permission: 'admin'             },
  { key: 'financial-management', icon: <FundOutlined />,              label: 'Financial Management', group: 'Finances',    permission: 'board-member'      },
  { key: 'statements',           icon: <FilePdfOutlined />,           label: 'Financial Statements', group: 'Finances',    permission: 'admin'             },
  { key: 'payments',             icon: <CreditCardOutlined />,        label: 'Payment Processing',   group: 'Finances',    permission: 'all'               },
  { key: 'reports-analytics',    icon: <BarChartOutlined />,          label: 'Analytics & Reports',  group: 'Finances',    permission: 'board-member'      },
  // Operations
  { key: 'maintenance-hub',      icon: <ToolOutlined />,              label: 'Maintenance',          group: 'Operations',  permission: 'board-member'      },
  { key: 'admin-operations',     icon: <ProjectOutlined />,           label: 'Operations Hub',       group: 'Operations',  permission: 'board-member'      },
  // Sprint 35 — finding G13. Was permission: 'all' which let owners
  // into the parking-spot + visitor-auth admin tooling — confusing
  // for residents who should use My Home > Visitors for their unit's
  // visitors. Restrict to board-member+ (admin, super-admin).
  { key: 'property-operations',  icon: <CarOutlined />,               label: 'Property Operations',  group: 'Operations',  permission: 'board-member'      },
  { key: 'amenities',            icon: <CalendarOutlined />,          label: 'Amenities',            group: 'Operations',  permission: 'all'               },
  { key: 'communications',       icon: <SoundOutlined />,             label: 'Communications',       group: 'Operations',  permission: 'all'               },
  { key: 'documents',            icon: <FileTextOutlined />,          label: 'Document Center',      group: 'Operations',  permission: 'all'               },
  // System
  { key: 'feature-permissions',  icon: <LockOutlined />,              label: 'Feature Permissions',  group: 'System',      permission: 'super_admin_condo' },
  // Sprint 39 — per-condo capability gates. Distinct from the
  // existing "Feature Permissions" surface (which controls per-role
  // sidebar visibility) — this one toggles the CondoCorpFeature
  // flags the backend gates on. Sits next to Feature Permissions so
  // a super-admin finds both in one place.
  { key: 'feature-toggles',      icon: <SettingOutlined />,           label: 'Feature Toggles',      group: 'System',      permission: 'super_admin_condo' },
];

export const MENU_GROUPS: MenuGroup[] = [
  { label: 'Overview',   key: 'Overview'   },
  { label: 'My Home',    key: 'My Home'    },
  { label: 'Property',   key: 'Property'   },
  { label: 'Residents',  key: 'Residents'  },
  { label: 'Finances',   key: 'Finances'   },
  { label: 'Operations', key: 'Operations' },
  { label: 'System',     key: 'System'     },
];

export const GROUP_COLORS: Record<MenuGroupKey, string> = {
  Overview:   '#6366f1',
  'My Home':  '#8b5cf6',
  Property:   '#0ea5e9',
  Residents:  '#14b8a6',
  Finances:   '#22c55e',
  Operations: '#f97316',
  System:     '#ef4444',
};

// --- Role-based gate ---------------------------------------------------------
// Mirrors the previous logic in MainDashboard. Kept here so menu visibility can
// be tested without rendering the full dashboard.
export const passesRoleGate = (
  permission: Permission,
  role: string | undefined,
): boolean => {
  if (permission === 'all') return true;
  if (permission === 'super_admin_condo') {
    return role === 'super_admin_condo' || role === 'superadmin';
  }
  if (permission === 'admin') {
    return role === 'admin' || role === 'super_admin_condo' || role === 'superadmin';
  }
  if (permission === 'owner') return role === 'owner';
  if (permission === 'board-member') {
    return role === 'board-member' || role === 'admin'
      || role === 'super_admin_condo' || role === 'superadmin';
  }
  return false;
};

// --- Feature-settings gate ---------------------------------------------------
// Super admin always passes. Anyone else: per-feature toggle in the
// authStore.featureSettings map (falls back to DEFAULT_FEATURE_SETTINGS).
export const passesFeatureGate = (
  key: string,
  role: string | undefined,
  featureSettings: Record<string, any> | undefined,
): boolean => {
  if (role === 'super_admin_condo' || role === 'superadmin') return true;
  const settings = featureSettings?.[key] ?? (DEFAULT_FEATURE_SETTINGS as any)[key];
  if (!settings) return true;
  if (role === 'admin') return settings.adminVisible;
  if (role === 'owner') return settings.ownerVisible;
  if (role === 'board-member') return settings.boardMemberVisible;
  return true;
};
